import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageSquareWarning, Loader2, Hash } from "lucide-react";

import GlobalMessageTab from "../../components/GenericChatTab.jsx";
import Avatar from "../../../../components/ui/Avatar.jsx";
import Skeleton from "../../../../components/ui/Skeleton.jsx";
import getSocket from "../../../../services/socket.js";
import formatRelativeTime from "../../../../utils/format-relative-time.js";
import { createConversationApi, chatTypeFromSlug } from "./api.js";

const CHAT_LABEL = {
  CLIENT_ADMIN: "Client-Admin",
  ADMIN_EMPLOYEE: "Admin-Employee",
};

// Project status → theme-token pill styles (light/dark safe)
const STATUS_STYLES = {
  TO_DO:                { bg: "bg-surface_alt", text: "text-text_secondary", dot: "bg-text_muted" },
  READY_FOR_ESTIMATING: { bg: "bg-info_bg",     text: "text-info",           dot: "bg-info" },
  IN_PROGRESS:          { bg: "bg-info_bg",     text: "text-info",           dot: "bg-info" },
  READY_FOR_REVIEW:     { bg: "bg-warning_bg",  text: "text-warning",        dot: "bg-warning" },
  IN_REVIEW:            { bg: "bg-warning_bg",  text: "text-warning",        dot: "bg-warning" },
  READY_FOR_REWORK:     { bg: "bg-high_bg",     text: "text-high",           dot: "bg-high" },
  DONE:                 { bg: "bg-success_bg",  text: "text-success",        dot: "bg-success" },
  CANCELED:             { bg: "bg-danger_bg",   text: "text-danger",         dot: "bg-danger" },
};

// History projects (latest status DONE / CANCELED) are view-only — no replying.
const HISTORY_STATUSES = ["DONE", "CANCELED"];

const humanizeStatus = (status) =>
  (status || "")
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const s = STATUS_STYLES[status] || STATUS_STYLES.TO_DO;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium
        ${s.bg} ${s.text} flex-shrink-0`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {humanizeStatus(status)}
    </span>
  );
};

export default function StandaloneChatPage() {
  const { chatType: slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const projectId = searchParams.get("project_id");
  const chatType = useMemo(() => chatTypeFromSlug(slug), [slug]);
  const api = useMemo(() => (chatType ? createConversationApi(chatType) : null), [chatType]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [online, setOnline] = useState(false);
  const [lastActive, setLastActive] = useState(null);

  useEffect(() => {
    if (!chatType) {
      setError("Unknown conversation type.");
      setLoading(false);
      return;
    }
    if (!projectId) {
      setError("Missing project_id in the URL.");
      setLoading(false);
      return;
    }

    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getMeta({ project_fid: Number(projectId) });
        if (!active) return;
        if (res.data?.status && res.data.data) {
          setMeta(res.data.data);
          setOnline(!!res.data.data.counterpart?.is_online);
          setLastActive(res.data.data.counterpart?.last_active || null);
        } else {
          setError(res.data?.message || "Unable to load this conversation.");
        }
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Unable to load this conversation.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [api, chatType, projectId]);

  // Live online/offline presence for the counterpart
  const counterpartId = meta?.counterpart?.id;
  useEffect(() => {
    if (!counterpartId) return;
    const socket = getSocket();
    if (!socket.connected) socket.connect();

    const handlePresence = ({ userId, isOnline }) => {
      if (Number(userId) === Number(counterpartId)) {
        setOnline(isOnline);
        if (!isOnline) setLastActive(new Date());
      }
    };

    socket.on("user-status-updated", handlePresence);
    return () => socket.off("user-status-updated", handlePresence);
  }, [counterpartId]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/swiftboard/live-projects");
  };

  const counterpart = meta?.counterpart;

  // Project object shape GenericChatTab expects (id used for the socket room)
  const project = meta ? { id: meta.project_id, title: meta.title } : null;

  // History projects open read-only here too (no composer / actions)
  const readOnly = HISTORY_STATUSES.includes(meta?.status);

  return (
    <div className="flex flex-col h-screen w-full bg-surface_alt">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 sm:px-5 h-16 bg-surface border-b border-border flex-shrink-0">
        <button
          onClick={handleBack}
          aria-label="Back"
          className="w-9 h-9 flex items-center justify-center rounded-full text-text_secondary
            hover:bg-surface_alt transition-colors flex-shrink-0"
        >
          <ArrowLeft size={20} />
        </button>

        {loading ? (
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10" rounded="rounded-full" />
            <div className="space-y-1.5">
              <Skeleton className="w-32 h-3" />
              <Skeleton className="w-20 h-2.5" />
            </div>
          </div>
        ) : meta ? (
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex-shrink-0">
              <Avatar user={counterpart} size={40} status={online ? "ONLINE" : "OFFLINE"} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-[15px] font-semibold text-text_primary truncate leading-tight">
                  {counterpart?.first_name} {counterpart?.last_name}
                </h1>
                {/* Live presence */}
                {online ? (
                  <span className="flex items-center gap-1 text-[11px] text-success flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-success" />
                    Online
                  </span>
                ) : lastActive ? (
                  <span className="text-[11px] text-text_muted flex-shrink-0 truncate">
                    last seen {formatRelativeTime(lastActive)}
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5 min-w-0 text-[11.5px]">
                <span className="px-1.5 py-px rounded-full bg-primary_tint text-primary text-[10px] font-medium flex-shrink-0">
                  {CHAT_LABEL[chatType]}
                </span>
                {meta.file_id != null && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-full
                    bg-surface_alt text-text_secondary text-[10px] font-medium flex-shrink-0">
                    <Hash size={10} />{meta.file_id}
                  </span>
                )}
                <span className="hidden sm:inline text-text_muted truncate">{meta.title}</span>
              </div>
            </div>

            {/* Project status badge — far right (hidden on phones for space) */}
            <div className="hidden sm:block flex-shrink-0 ml-auto">
              <StatusBadge status={meta.status} />
            </div>
          </div>
        ) : null}
      </header>

      {/* Body */}
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center text-text_muted gap-3">
            <Loader2 size={28} className="animate-spin text-primary" />
            <p className="text-[13px]">Loading conversation…</p>
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 fade-in">
            <div className="w-16 h-16 rounded-full bg-danger_bg flex items-center justify-center mb-4">
              <MessageSquareWarning size={28} className="text-danger" />
            </div>
            <h2 className="text-base font-medium text-text_primary mb-1.5">Conversation unavailable</h2>
            <p className="text-[13px] text-text_muted max-w-sm mb-5">{error}</p>
            <button
              onClick={handleBack}
              className="px-4 py-2 text-[13px] font-medium text-white bg-primary rounded-[10px]
                hover:bg-primary_hover transition-colors"
            >
              Go back
            </button>
          </div>
        ) : (
          <GlobalMessageTab
            project={project}
            api={api}
            withChat={counterpart}
            chatType={chatType}
            standalone
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
}
